import { v4 as uuid } from 'uuid';

import { CapturedVideoData } from '.';

type MediaRecorderClientEvent = 'start' | 'stop' | 'error' | 'time-exceeded';

/**
 * 1Mbps roughly translates to 360p
 */
export const VIDEO_BITS_PER_SECOND = 1_000_000;

interface MediaRecorderClientSubscriber {
  event: MediaRecorderClientEvent;
  callback: MediaRecorderListener;
}

type MediaRecorderListener =
  | ((isRestart: boolean) => void)
  | ((videoCaptureData: CapturedVideoData, elapsedTime: number) => void)
  | ((e: string | Error) => void)
  | (() => void);

export interface MediaRecorderClientBaseParams {
  stream: MediaStream;
  bitRate?: number;
  minRecordingTime?: number;
  onStart?: (isRestart: boolean) => void;
  onRecordingComplete?: (videoCaptureData: CapturedVideoData, elapsedTime: number) => void;
  // Note that these types are best-effort, since `notify` erases the types.
  onError?: (e: string | Error) => void;
  onTimeExceeded?: () => void;
}

/**
 * The minimum amount of time a recording can be -- so we don't end up sending videos that are only a few milliseconds long
 */
const MIN_RECORDING_TIME = 5000;

/**
 * Interface for handling an instance of a MediaRecorder. This Client is a singleton that uses an Observer model so it can
 * live across multiple routes / views
 */
export abstract class BaseMediaRecorderClient<ParamsType extends MediaRecorderClientBaseParams> {
  protected params: ParamsType | null = null;
  private timeout: ReturnType<typeof setTimeout> | null = null;
  private startTime: number | null = null;
  private subscribers: Record<string, MediaRecorderClientSubscriber[]> = {};

  public subscribe(subscribers: {
    onStart?: (isRestart: boolean) => void;
    onRecordingComplete?: (videoCaptureData: CapturedVideoData, elapsedTime: number) => void;
    onError?: (e: string | Error) => void;
    onTimeExceeded?: () => void;
  }): string {
    const id = uuid();
    const internalSubs: MediaRecorderClientSubscriber[] = [];

    if (subscribers.onStart) {
      internalSubs.push({ event: 'start', callback: subscribers.onStart });
    }

    if (subscribers.onRecordingComplete) {
      internalSubs.push({
        event: 'stop',
        callback: subscribers.onRecordingComplete,
      });
    }

    if (subscribers.onError) {
      internalSubs.push({ event: 'error', callback: subscribers.onError });
    }

    if (subscribers.onTimeExceeded) {
      internalSubs.push({ event: 'time-exceeded', callback: subscribers.onTimeExceeded });
    }

    this.subscribers[id] = internalSubs;

    return id;
  }

  public unsubscribe(subscriptionId: string | null | undefined) {
    if (subscriptionId) {
      delete this.subscribers[subscriptionId];
    }

    return this;
  }

  abstract startRecording(): void;
  abstract stopCapture(): void;
  abstract destroy(): void;

  abstract isRecording(): boolean;
  abstract isSupported(): boolean;

  protected onStartedRecording = () => {
    const isRestart = !!this.startTime;
    this.startTime = Date.now();
    this.notify('start', isRestart);
  };

  protected onStoppedRecording = (videoData: CapturedVideoData) => {
    this.notify('stop', videoData, this.getElapsedTime());
    this.destroy();
  };

  protected getElapsedTime = () => {
    return this.startTime ? Date.now() - this.startTime : 0;
  };

  public stopRecording = () => {
    if (!this.timeout) {
      // sometimes startTime is not defined yet if `handleStart` was delayed
      const timeElapsed = this.startTime != null ? Date.now() - this.startTime : 0;

      // add a delay so we can collect a video that is at least MIN_RECORDING_TIME long
      const minRecordingTime = this.params?.minRecordingTime ?? MIN_RECORDING_TIME;
      const delay = timeElapsed < minRecordingTime ? minRecordingTime - timeElapsed : 0;
      this.timeout = setTimeout(() => {
        this.stopCapture();
      }, delay);
    }

    return this;
  };

  public get stream(): MediaStream | undefined {
    return this.params?.stream;
  }

  protected clearTimeouts = () => {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  };

  protected clearSubscribers = () => {
    this.subscribers = {};
  };

  protected notify = (event: MediaRecorderClientEvent, ...args: unknown[]) => {
    const subs = Object.values(this.subscribers)
      .flat()
      .filter((sub) => sub.event === event);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS2684 - see https://stackoverflow.com/questions/65245161/typescript-error-the-this-context-of-type-is-not-assignable-to-methods
    subs.forEach((sub) => sub.callback?.apply(null, args));
  };
}
